// ─────────────────────────────────────────────────────────────────────────────
// QUÉ PUEDE VER UN CLIENTE DE SU PROPIA FICHA
//
// ── EL DEFECTO, MEDIDO EL 2026-10-06 ──────────────────────────────────────
//
// `clientes` tiene 38 columnas. `db.clientes.getAll()` pedía `select('*')`, así que
// el navegador de un cliente recibía su fila COMPLETA: las notas internas que el
// estudio escribió sobre él, la confianza de atribución de su adquisición, su etapa
// de pipeline, su diseñador asignado, el motivo por el que se archivó.
//
// RLS no protege de esto. Gobierna QUÉ FILAS se ven —y ahí hace su trabajo: un
// cliente ve una, la suya— pero no qué columnas. La minimización es una decisión de
// la consulta, y por eso tiene que estar escrita.
//
// ── POR QUÉ DOS LISTAS Y NO UN FILTRO ─────────────────────────────────────
//
// Se podría quitar lo interno después de recibirlo. No sirve: el dato ya cruzó la
// red, ya está en la pestaña del cliente y ya quedó en el caché del navegador.
// Minimizar es no pedirlo.
//
// `COLUMNAS_SEGURAS_PARA_EL_CLIENTE` es la lista POSITIVA —lo que se pide— y
// `COLUMNAS_SOLO_INTERNAS` la negativa, que existe para que una guarda pueda
// comprobar que las dos no se cruzan. Una lista positiva sola no detecta que alguien
// añadió `notas`; con las dos, añadirlo rompe una prueba.
//
// ── LO QUE ESTO NO CAMBIA ─────────────────────────────────────────────────
//
// El personal sigue recibiendo lo que necesita. CRM vive de `stage` y de la
// atribución, Kanban de `creativo_id`, Marketing de las fuentes: recortarles la
// consulta sería romper el panel para proteger a quien ya tiene permiso. La
// separación es por QUIÉN MIRA, no por tabla.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lo que un cliente necesita de su propia ficha, y nada más.
 *
 * Las 17 primeras son las que consume el portal —medido sobre los 43 ficheros de
 * `ClientPortal` y `features/client-portal`—. Las dos últimas no se pintan: las
 * necesita la decisión de acceso (`decidirAccesoDeCliente` lee `archived_at` y
 * `portal_access_status`), y sin ellas el portal abriría una ficha archivada.
 */
export const COLUMNAS_SEGURAS_PARA_EL_CLIENTE = Object.freeze([
  'id',
  'nombre',
  'tipo',
  'email',
  'brand',
  'servicio',
  'monto',
  'is_demo',
  'portal_user_id',
  'portal_access_created_at',
  'portal_access_sent_at',
  'portal_access_status',
  'portal_access_whatsapp_sent_at',
  'whatsapp',
  'telefono',
  'empresa',
  'service_type',
  'archived_at',
  'portal_password_must_change',
]);

/**
 * Operación del estudio. Ninguna de éstas es asunto del cliente sobre sí mismo:
 * son lo que el estudio anota, clasifica o decide internamente.
 */
export const COLUMNAS_SOLO_INTERNAS = Object.freeze([
  'notas',
  'acquisition_notes',
  'acquisition_confidence',
  'acquisition_source_id',
  'acquisition_campaign_id',
  'original_lead_id',
  'archive_reason',
  'archived_by',
  'stage',
  'creativo_id',
  'sector',
  'sector_origen',
  'sector_actualizado_en',
  'email_declarado_evidencia',
]);

/** La lista de columnas, ya en la forma que espera PostgREST. */
export const SELECT_SEGURO_PARA_EL_CLIENTE = COLUMNAS_SEGURAS_PARA_EL_CLIENTE.join(',');

/**
 * El `select` que corresponde a quien mira.
 *
 * El personal recibe lo de siempre —incluido el `team` embebido, que el panel usa
 * para pintar al diseñador—; un cliente recibe sólo su mínimo. Se decide aquí y no
 * en cada pantalla: una decisión de privacidad repartida en diez sitios es una
 * decisión que se olvida en el undécimo.
 *
 * @param {boolean} esCliente  `true` cuando quien mira es un cliente del portal.
 */
export function selectDeClientes(esCliente = false) {
  return esCliente
    ? SELECT_SEGURO_PARA_EL_CLIENTE
    : '*, team:creativo_id(name, initials, color, bg)';
}

export default selectDeClientes;
